/*
 * OC Recruitment — About OC Recruitment
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * No founder photo. Text-only. Professional but human.
 */

import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { pageSEO, organizationSchema, personSchema, ENTITY_DESCRIPTION } from "@/lib/seoConfig";

const milestones = [
  {
    stat: "16+",
    label: "Years in Packaging Recruitment",
    description:
      "Over sixteen years dedicated exclusively to recruiting within the packaging industry — building knowledge, relationships, and a reputation for delivering results.",
  },
  {
    stat: "8",
    label: "Packaging Sectors Covered",
    description:
      "Flexible packaging, rigid plastics, corrugated packaging, folding carton, labels & sleeves, glass packaging, metal packaging, and caps & closures — a comprehensive understanding of the full packaging landscape.",
  },
  {
    stat: "100%",
    label: "US Market Focus",
    description:
      "Operating entirely within the United States packaging market, bringing a focused perspective to packaging sales recruitment.",
  },
  {
    stat: "Sales",
    label: "Exclusive Commercial Focus",
    description:
      "Recruiting exclusively for sales and commercial leadership roles. This singular focus means a deeper network and a sharper understanding of what makes great packaging sales talent.",
  },
];

export default function AboutChris() {
  return (
    <div>
      <SEO
        title={pageSEO.about.title}
        description={pageSEO.about.description}
        keywords={pageSEO.about.keywords}
        canonical={pageSEO.about.canonical}
        structuredData={{ "@context": "https://schema.org", "@graph": [organizationSchema, personSchema] }}
      />
      {/* Hero */}
      <section className="bg-midnight pt-32 pb-20">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                About the Firm
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                About OC Recruitment
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                {ENTITY_DESCRIPTION}
              </p>
              <p className="text-sm text-white/50 mt-5">OC Recruitment, formerly OC International.</p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Main Content — no photo */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="max-w-3xl">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-8">
                Founded on Industry Expertise
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <div className="space-y-6 mb-12">
                <p className="text-slate-brand leading-relaxed">
                  OC Recruitment was founded by Chris Kenny, a specialist
                  recruiter with over 16 years of experience within the US
                  packaging industry.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  The firm focuses exclusively on placing sales and commercial
                  leadership professionals across the United States packaging
                  sector.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  OC Recruitment partners with packaging manufacturers and
                  distributors to identify and secure high-impact commercial
                  talent.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <h3 className="text-2xl text-midnight mb-6 font-['DM_Serif_Display',Georgia,serif]">
                Deep Industry Knowledge
              </h3>
              <div className="space-y-6 mb-12">
                <p className="text-slate-brand leading-relaxed">
                  Over the course of his career, Chris has built an extensive
                  network of sales professionals, hiring managers, and industry
                  leaders across every major packaging segment in the United
                  States. From flexible packaging and rigid plastics manufacturers on the West Coast to
                  corrugated packaging producers in the Midwest, from glass packaging
                  companies in the South to labels & sleeves converters in the Northeast — his reach
                  spans the full geographic and sectoral breadth of the US
                  packaging market.
                </p>
                <p className="text-slate-brand leading-relaxed">
                  This focused positioning provides a unique perspective — one
                  that combines the rigor and professionalism of the executive
                  search tradition with deep, practical knowledge of the American
                  packaging landscape.
                </p>
              </div>
            </ScrollReveal>


            <ScrollReveal delay={0.25}>
              <div className="space-y-6 mb-12">
                <p className="text-slate-brand leading-relaxed">
                  Chris's approach is straightforward: understand the client's
                  business, know the market, identify the right people, and
                  manage the process with professionalism and transparency. There
                  are no gimmicks, no exaggerated promises — just consistent,
                  specialist recruitment delivered by someone who has dedicated
                  their career to this industry.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* Stats */}
          <ScrollReveal delay={0.3}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {milestones.map((m, i) => (
                <div
                  key={m.label}
                  className="p-6 bg-pale rounded border border-transparent hover:border-border transition-colors"
                >
                  <span className="text-3xl font-['DM_Serif_Display',Georgia,serif] text-midnight block mb-1">
                    {m.stat}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-steel block mb-3">
                    {m.label}
                  </span>
                  <p className="text-sm text-slate-brand leading-relaxed">
                    {m.description}
                  </p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-pale">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-midnight mb-6">
                Let's Have a Conversation
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-slate-brand leading-relaxed mb-10">
                Whether you are a packaging company looking for sales talent or a
                commercial professional exploring your next opportunity, Chris is
                available for a confidential discussion.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-midnight text-white text-sm font-semibold rounded hover:bg-midnight/90 transition-all duration-200 group"
              >
                Speak With Chris
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
